import React, { useMemo, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import ModernItemEditor from './ModernItemEditor';

function formatMinutes(minutes = 0) {
  const safeMinutes = Math.max(0, Number(minutes) || 0);
  const hours = Math.floor(safeMinutes / 60);
  const remainder = safeMinutes % 60;
  if (!hours) return `${remainder}m`;
  if (!remainder) return `${hours}h`;
  return `${hours}h ${String(remainder).padStart(2, '0')}m`;
}

function statusTone(statusKey) {
  if (statusKey === 'feito') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (statusKey === 'em_progresso') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (statusKey === 'parado') return 'border-rose-200 bg-rose-50 text-rose-700';
  return 'border-slate-200 bg-slate-100 text-slate-600';
}

function priorityTone(priorityKey) {
  if (priorityKey === 'critica' || priorityKey === 'alta') return 'bg-rose-500 text-white';
  if (priorityKey === 'media') return 'bg-amber-400 text-slate-950';
  if (priorityKey === 'baixa') return 'bg-emerald-500 text-white';
  return 'bg-slate-300 text-slate-700';
}

function legacyAssignees(item) {
  const values = item?.column_values || {};
  if (Array.isArray(values.persons) && values.persons.length) {
    return values.persons.map((person, index) =>
      typeof person === 'string'
        ? { id: `${person}-${index}`, name: person }
        : { id: person.id || person.user_id || person.email || `${person.name}-${index}`, name: person.name || person.email || 'Usuário' }
    );
  }
  if (values.person) return [{ id: values.person_id || values.person, name: values.person }];
  return [];
}

function fallbackGroups(groups, items) {
  return (groups || []).map(group => ({
    ...group,
    name: group.title,
    items: (items || [])
      .filter(item => item.group_id === group.id)
      .map(item => ({
        ...item,
        rollup: {
          status_key: item.column_values?.status || 'pendente',
          status_label: String(item.column_values?.status || 'Sem status').replace(/_/g, ' '),
          priority_key: item.column_values?.priority || 'sem_prioridade',
          priority_label: String(item.column_values?.priority || 'Sem prioridade').replace(/_/g, ' '),
          subtasks_total: 0,
          subtasks_completed: 0,
          blocked_subtasks: 0,
          overdue_subtasks: 0,
          completion_percentage: 0,
          planned_minutes: 0,
          tracked_minutes: 0,
          assignees: legacyAssignees(item),
          next_due_date: item.column_values?.due_date || null,
          subtasks: [],
        },
      })),
  }));
}

export default function ProjectKanban({ board, groups, items, members, insights, customColumns = [], api, darkMode = false, onDataChange }) {
  const [newItem, setNewItem] = useState('');
  const [addingToGroup, setAddingToGroup] = useState(null);
  const [selectedItemForComments, setSelectedItemForComments] = useState(null);
  const [showCommentsModal, setShowCommentsModal] = useState(false);

  const groupedItems = useMemo(
    () => (insights?.groups?.length ? insights.groups : fallbackGroups(groups, items)),
    [groups, insights, items]
  );

  const handleAddItem = async (groupId, itemName) => {
    if (!itemName.trim()) return;
    try {
      await api.post('/projects/items', {
        board_id: board.id,
        group_id: groupId,
        name: itemName.trim(),
        column_values: {},
        position: 0,
      });
      setNewItem('');
      setAddingToGroup(null);
      onDataChange();
      toast.success('Item adicionado com sucesso!');
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Erro ao adicionar item');
    }
  };

  const handleAddGroup = async () => {
    const groupName = prompt('Nome do grupo:');
    if (!groupName) return;
    try {
      await api.post('/projects/groups', {
        board_id: board.id,
        title: groupName,
        position: groups.length,
      });
      onDataChange();
      toast.success('Grupo adicionado com sucesso!');
    } catch (error) {
      console.error('Error adding group:', error);
      toast.error('Erro ao adicionar grupo');
    }
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    try {
      if (destination.droppableId !== source.droppableId) {
        await api.put(`/projects/items/${draggableId}`, {
          group_id: destination.droppableId,
          position: destination.index,
        });
      } else {
        await api.put(`/projects/items/${draggableId}`, {
          position: destination.index,
        });
      }
      onDataChange();
      toast.success('Item movido com sucesso!');
    } catch (error) {
      console.error('Error moving item:', error);
      toast.error('Erro ao mover item');
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <section className="min-h-[calc(100vh-190px)] px-3 py-4 sm:px-5 lg:px-7">
        <div className="mx-auto w-full max-w-none space-y-5">
          <header className="flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Kanban operacional</p>
              <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">{board?.name || 'Quadro'}</h2>
              <p className="mt-1 text-sm text-slate-500">
                Cards com rollup de subtarefas, responsáveis, prazo e esforço planejado.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                {insights?.summary?.total_items || items?.length || 0} itens no board
              </div>
              <Button type="button" variant="outline" onClick={handleAddGroup}>Novo grupo</Button>
            </div>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {groupedItems.map(group => (
              <section key={group.id} className="min-w-0">
                <div className="rounded-3xl border border-slate-200 bg-slate-50/80 shadow-sm">
                  <div className="border-b border-slate-200 bg-white px-4 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: group.color || '#3b82f6' }} />
                          <h3 className="font-semibold text-slate-900">{group.name || group.title}</h3>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          {group.total_items ?? group.items?.length ?? 0} itens · {group.subtasks ?? 0} subtarefas
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAddingToGroup(group.id)}
                        className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                      >
                        + Item
                      </button>
                    </div>
                  </div>

                  {addingToGroup === group.id && (
                    <div className="border-b border-slate-200 bg-white/80 px-4 py-4">
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-3">
                        <Input
                          placeholder="Digite o nome da tarefa..."
                          value={newItem}
                          onChange={event => setNewItem(event.target.value)}
                          onKeyDown={event => {
                            if (event.key === 'Enter') handleAddItem(group.id, newItem);
                          }}
                          className="border-slate-200"
                        />
                        <div className="mt-3 flex gap-2">
                          <Button type="button" size="sm" onClick={() => handleAddItem(group.id, newItem)}>
                            Criar
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => setAddingToGroup(null)}>
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  <Droppable droppableId={group.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`min-h-[320px] space-y-3 p-4 transition-colors ${
                          snapshot.isDraggingOver ? 'bg-blue-50/60' : ''
                        }`}
                      >
                        {(group.items || []).map((item, index) => {
                          const rollup = item.rollup || {};
                          const assignees = rollup.assignees || legacyAssignees(item);
                          const dueDate = rollup.next_due_date || rollup.latest_due_date;
                          const subtasksTotal = rollup.subtasks_total || 0;
                          const subtasksCompleted = rollup.subtasks_completed || 0;
                          const progress = rollup.completion_percentage || 0;

                          return (
                            <Draggable key={item.id} draggableId={item.id} index={index}>
                              {(dragProvided, snapshotState) => (
                                <article
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                  className={`rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm transition-transform ${
                                    snapshotState.isDragging ? 'rotate-1 shadow-xl cursor-grabbing' : 'cursor-grab hover:-translate-y-0.5 hover:shadow-md'
                                  }`}
                                >
                                  <div className="space-y-4">
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setSelectedItemForComments(item);
                                          setShowCommentsModal(true);
                                        }}
                                        className="min-w-0 flex-1 text-left"
                                      >
                                        <h4 className="line-clamp-2 text-sm font-semibold text-slate-900 hover:text-blue-700">
                                          {item.name}
                                        </h4>
                                      </button>
                                      <div className="flex shrink-0 items-start gap-2">
                                        <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-semibold ${priorityTone(rollup.priority_key)}`}>
                                          {rollup.priority_label || 'Sem prioridade'}
                                        </span>
                                        <div
                                          className="flex shrink-0 select-none items-center rounded-xl border border-slate-200 bg-slate-50 px-2 py-1 text-slate-400"
                                          aria-hidden="true"
                                        >
                                          <span className="text-xs tracking-[-0.2em]">•••</span>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                      <span className={`rounded-full border px-3 py-1 text-[11px] font-medium ${statusTone(rollup.status_key)}`}>
                                        {rollup.status_label || 'Sem status'}
                                      </span>
                                      {rollup.overdue_subtasks > 0 && (
                                        <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-700">
                                          {rollup.overdue_subtasks} atrasadas
                                        </span>
                                      )}
                                      {rollup.blocked_subtasks > 0 && (
                                        <span className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-[11px] font-medium text-rose-700">
                                          {rollup.blocked_subtasks} bloqueios
                                        </span>
                                      )}
                                    </div>

                                    <div className="space-y-3">
                                      <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div className="rounded-xl bg-slate-50 px-3 py-2">
                                          <p className="text-slate-400">Planejado</p>
                                          <p className="mt-1 font-semibold text-slate-800">{formatMinutes(rollup.planned_minutes)}</p>
                                        </div>
                                        <div className="rounded-xl bg-slate-50 px-3 py-2">
                                          <p className="text-slate-400">Rastreado</p>
                                          <p className="mt-1 font-semibold text-emerald-700">{formatMinutes(rollup.tracked_minutes)}</p>
                                        </div>
                                      </div>

                                      {subtasksTotal > 0 && (
                                        <div>
                                          <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
                                            <span>Subtarefas concluídas</span>
                                            <span className="font-semibold text-slate-700">
                                              {subtasksCompleted}/{subtasksTotal}
                                            </span>
                                          </div>
                                          <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                                            <div className="h-full rounded-full bg-blue-600" style={{ width: `${progress}%` }} />
                                          </div>
                                        </div>
                                      )}

                                      <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="flex -space-x-2">
                                          {assignees.length ? assignees.slice(0, 4).map(person => (
                                            <span
                                              key={person.id}
                                              title={person.name}
                                              className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-gradient-to-br from-blue-500 to-indigo-600 text-[10px] font-bold text-white"
                                            >
                                              {person.name?.charAt(0)?.toUpperCase() || 'U'}
                                            </span>
                                          )) : (
                                            <span className="rounded-full border border-dashed border-slate-300 px-3 py-1 text-[11px] text-slate-500 whitespace-nowrap">
                                              Sem responsável
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-right text-xs text-slate-500">
                                          <p>{dueDate ? new Date(`${String(dueDate).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : 'Sem prazo'}</p>
                                          <p className="mt-1 font-medium text-slate-700">
                                            {subtasksTotal > 0 ? `${progress}% consolidado` : 'Sem subtarefas'}
                                          </p>
                                        </div>
                                      </div>

                                      {subtasksTotal > 0 && (
                                        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
                                          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Execução interna</p>
                                          <div className="mt-2 space-y-2">
                                            {(rollup.subtasks || []).slice(0, 3).map(subtask => (
                                              <div key={subtask.id} className="flex items-center justify-between gap-3 text-xs">
                                                <span className="truncate text-slate-600">↳ {subtask.name}</span>
                                                <span className={`rounded-full border px-2 py-0.5 ${statusTone(subtask.status_key)}`}>
                                                  {subtask.status_label}
                                                </span>
                                              </div>
                                            ))}
                                            {(rollup.subtasks || []).length > 3 && (
                                              <p className="text-[11px] font-medium text-blue-600">
                                                + {(rollup.subtasks || []).length - 3} subtarefa(s)
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      <div className="flex justify-end">
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            setSelectedItemForComments(item);
                                            setShowCommentsModal(true);
                                          }}
                                        >
                                          Abrir tarefa
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </article>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}

                        {!group.items?.length && (
                          <div className="rounded-2xl border border-dashed border-slate-300 bg-white/70 px-4 py-10 text-center text-sm text-slate-500">
                            Nenhuma tarefa neste grupo.
                          </div>
                        )}
                      </div>
                    )}
                  </Droppable>
                </div>
              </section>
            ))}

            <div className="min-w-0">
              <button
                type="button"
                onClick={handleAddGroup}
                className="flex h-full min-h-[180px] w-full items-center justify-center rounded-3xl border-2 border-dashed border-slate-300 bg-white text-sm font-semibold text-slate-500 transition-colors hover:border-blue-400 hover:text-blue-600"
              >
                + Novo grupo
              </button>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={showCommentsModal} onOpenChange={setShowCommentsModal}>
        <DialogContent
          overlayClassName="bg-slate-950/35 backdrop-blur-sm"
          className={`flex h-[88vh] max-h-[88vh] w-[94vw] max-w-none flex-col overflow-hidden rounded-2xl p-0 shadow-2xl sm:h-[85vh] sm:max-h-[85vh] sm:w-[85vw] ${darkMode ? 'nexo-project-dark bg-slate-950' : 'bg-slate-50'}`}
        >
          <DialogHeader className="shrink-0 border-b border-slate-200 bg-white px-6 py-4">
            <DialogTitle className="text-sm font-semibold text-slate-500">Detalhe da tarefa</DialogTitle>
          </DialogHeader>

          {selectedItemForComments && (
            <ModernItemEditor
              item={selectedItemForComments}
              api={api}
              members={members}
              allItems={items}
              subtasks={selectedItemForComments.rollup?.subtasks || []}
              parentItem={selectedItemForComments.parent_id ? items.find(entry => entry.id === selectedItemForComments.parent_id) : null}
              customColumns={customColumns}
              darkMode={darkMode}
              onClose={() => setShowCommentsModal(false)}
              onItemUpdate={(updatedItem) => {
                if (updatedItem?.id) {
                  setSelectedItemForComments(current => current?.id === updatedItem.id ? { ...current, ...updatedItem } : current);
                }
                onDataChange();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </DragDropContext>
  );
}
